import React, { useState, useEffect } from 'react';
import { ApiURLs } from '../../constant/ApiUrls';
import { fetchData } from '../../services/api';
import { cur } from '../../constant/Constants';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF4C4C', '#1E90FF', '#32CD32', '#FFD700', '#8A2BE2'];

const HealthBadge = ({ score, status }) => {
    const color = status === 'green' ? '#16a34a' : status === 'amber' ? '#d97706' : '#dc2626';
    const bg = status === 'green' ? '#dcfce7' : status === 'amber' ? '#fef3c7' : '#fee2e2';
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 50, backgroundColor: bg, border: `2px solid ${color}` }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
            <span style={{ fontWeight: 700, fontSize: 18, color }}>{score}/100</span>
            <span style={{ fontSize: 13, color, textTransform: 'uppercase', fontWeight: 600 }}>
                {status === 'green' ? 'Healthy' : status === 'amber' ? 'Monitor' : 'Action Required'}
            </span>
        </div>
    );
};

const KpiCard = ({ label, value, prefix = '', suffix = '', color = '#1a1a2e' }) => (
    <div style={{
        background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0', minWidth: 160, flex: '1 1 180px',
    }}>
        <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color }}>{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</div>
    </div>
);

const SectionTitle = ({ children }) => (
    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginTop: 30, marginBottom: 14, borderBottom: '2px solid #e5e7eb', paddingBottom: 8 }}>{children}</h3>
);

const MarketplacePulse = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetchData(ApiURLs.MARKETPLACE_PULSE);
                if (response.status === 1) {
                    setData(response.data);
                    setLastUpdated(new Date().toLocaleString());
                }
            } catch (e) {
                console.error('Pulse error:', e);
            }
            setLoading(false);
        };
        load();
        const interval = setInterval(load, 60000);
        return () => clearInterval(interval);
    }, []);

    if (loading) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading Marketplace Pulse...</div>;
    if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Failed to load data</div>;

    return (
        <div style={{ padding: '20px 24px', maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a1a2e' }}>Marketplace Pulse</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
                        Last updated: {lastUpdated} &bull; Auto-refreshes every 60s
                    </p>
                </div>
                <HealthBadge score={data.health_score} status={data.health_status} />
            </div>

            {/* USER KPIs */}
            <SectionTitle>Users</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <KpiCard label="Total Users" value={data.users.total} />
                <KpiCard label="New Today" value={data.users.new_today} color="#16a34a" />
                <KpiCard label="Yesterday" value={data.users.new_yesterday} />
                <KpiCard label="Last 7 Days" value={data.users.new_7d} color="#0088FE" />
                <KpiCard label="Last 30 Days" value={data.users.new_30d} color="#0088FE" />
                <KpiCard label="This Year" value={data.users.new_year} />
                <KpiCard label="Dealers" value={data.users.dealers} color="#d97706" />
                <KpiCard label="Blocked" value={data.users.blocked} color="#dc2626" />
            </div>

            {/* LISTING KPIs */}
            <SectionTitle>Listings</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <KpiCard label="Live Listings" value={data.listings.total_live} color="#16a34a" />
                <KpiCard label="Total Ever" value={data.listings.total_ever} />
                <KpiCard label="New Today" value={data.listings.new_today} color="#16a34a" />
                <KpiCard label="Yesterday" value={data.listings.new_yesterday} />
                <KpiCard label="Last 7 Days" value={data.listings.new_7d} color="#0088FE" />
                <KpiCard label="Last 30 Days" value={data.listings.new_30d} color="#0088FE" />
                <KpiCard label="Sold" value={data.listings.sold} color="#d97706" />
                <KpiCard label="Expired" value={data.listings.expired} color="#dc2626" />
                <KpiCard label="Avg Days to Sell" value={data.listings.avg_time_to_sell} suffix=" days" />
            </div>

            {/* REVENUE KPIs */}
            <SectionTitle>Revenue</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <KpiCard label="Today" value={data.revenue.today} prefix={cur} color="#16a34a" />
                <KpiCard label="Last 7 Days" value={data.revenue.last_7d} prefix={cur} color="#0088FE" />
                <KpiCard label="Last 30 Days" value={data.revenue.last_30d} prefix={cur} color="#0088FE" />
                <KpiCard label="This Year" value={data.revenue.this_year} prefix={cur} />
                <KpiCard label="All Time" value={data.revenue.all_time} prefix={cur} color="#1a1a2e" />
                <KpiCard label="Spotlight Revenue" value={data.revenue.spotlight} prefix={cur} color="#d97706" />
            </div>

            {/* ENGAGEMENT KPIs */}
            <SectionTitle>Engagement</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <KpiCard label="Total Conversations" value={data.engagement.total_conversations} />
                <KpiCard label="Conversations Today" value={data.engagement.conversations_today} color="#16a34a" />
                <KpiCard label="Total Offers" value={data.engagement.total_offers} />
                <KpiCard label="Offers Today" value={data.engagement.offers_today} color="#16a34a" />
                <KpiCard label="Reported Ads" value={data.engagement.reported_ads} color="#dc2626" />
                <KpiCard label="Reported Today" value={data.engagement.reported_today} color="#dc2626" />
            </div>

            {/* CHARTS */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 30 }}>
                {/* Top Categories */}
                <div style={{ flex: '1 1 400px', background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Top Categories</h4>
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={data.top_categories} layout="vertical" margin={{ left: 0, right: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis dataKey="category_name" type="category" width={130} style={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3b82f6" barSize={18} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Top Locations */}
                <div style={{ flex: '1 1 350px', background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Top Locations</h4>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie data={data.top_locations} dataKey="count" nameKey="location" cx="50%" cy="50%" outerRadius={80} label>
                                {data.top_locations.map((entry, index) => (
                                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                        {data.top_locations.map((entry, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                                <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: COLORS[index % COLORS.length] }} />
                                {entry.location}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* RECENT ACTIVITY */}
            <SectionTitle>Recent Activity</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                <div style={{ flex: '1 1 400px', background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Latest Ads</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>ID</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Title</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.recent_ads.map((ad) => (
                                <tr key={ad.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '6px 8px' }}>{ad.id}</td>
                                    <td style={{ padding: '6px 8px' }}>{ad.title?.substring(0, 30)}</td>
                                    <td style={{ padding: '6px 8px' }}>
                                        <span style={{
                                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                            backgroundColor: ad.status === 1 ? '#dcfce7' : ad.status === 2 ? '#fef3c7' : '#fee2e2',
                                            color: ad.status === 1 ? '#16a34a' : ad.status === 2 ? '#d97706' : '#dc2626',
                                        }}>
                                            {ad.status === 1 ? 'Live' : ad.status === 2 ? 'Sold' : ad.status === 4 ? 'Deleted' : 'Other'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '6px 8px', fontSize: 11, color: '#888' }}>
                                        {new Date(ad.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={{ flex: '1 1 400px', background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Latest Users</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>ID</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Name</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Email</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Joined</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.recent_users.map((user) => (
                                <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '6px 8px' }}>{user.id}</td>
                                    <td style={{ padding: '6px 8px' }}>{user.name}</td>
                                    <td style={{ padding: '6px 8px', fontSize: 11 }}>{user.email}</td>
                                    <td style={{ padding: '6px 8px', fontSize: 11, color: '#888' }}>
                                        {new Date(user.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MarketplacePulse;
